import { createBaseQueryWithReauth } from 'store/auth/base-query-with-reauth'
import {
  UploadLoadConfirmationResult,
  UploadLoadConfirmationPayload,
  LoadConfirmationPresignedUrlResult,
  LoadConfirmationPresignedUrlPayload,
  PendingLoadConfirmationsResult,
  PendingLoadConfirmationsPayload,
  UploadLogoResponse,
  UploadLogoPayload,
  DeletePendingLoadConfirmationPayload,
  DeletePendingLoadConfirmationResult,
  UploadLoadConfirmationToOrderPayload,
  UploadLoadConfirmationToOrderResult,
  EnsureInvoiceForOrderPayload,
  EnsureInvoiceForOrderResult,
  LcReprocessApplyPayload,
  LcReprocessApplyResult,
  LcReprocessCancelPayload,
  LcReprocessCancelResult,
  LcReprocessUploadPayload,
  LcReprocessUploadResult,
} from './task-automation.types'
import { createApi } from '@reduxjs/toolkit/query/react'

const baseUrl = `${process.env.REACT_APP_TASK_AUTOMATION_SERVICE_API}`

export const taskAutomationApi = createApi({
  reducerPath: 'taskAutomationApi',
  baseQuery: createBaseQueryWithReauth(baseUrl!),
  endpoints: (builder) => ({
    uploadLoadConfirmation: builder.mutation<
      UploadLoadConfirmationResult,
      UploadLoadConfirmationPayload
    >({
      query: ({ file, userId }) => {
        const formData = new FormData()
        formData.append('file', file)
        return {
          url: 'tapi/v1/docs/load-confirmation/upload',
          method: 'POST',
          body: formData,
          headers: userId ? { userId } : undefined,
        }
      },
    }),

    uploadLoadConfirmationToOrder: builder.mutation<
      UploadLoadConfirmationToOrderResult,
      UploadLoadConfirmationToOrderPayload
    >({
      query: ({ orderId, file }) => {
        const formData = new FormData()
        formData.append('file', file)
        return {
          url: `tapi/v1/docs/load-confirmation/orders/${orderId}/direct-upload`,
          method: 'POST',
          body: formData,
        }
      },
    }),

    reprocessLoadConfirmationForOrder: builder.mutation<
      LcReprocessUploadResult,
      LcReprocessUploadPayload
    >({
      query: ({ orderId, file, invoiceId }) => {
        const formData = new FormData()
        formData.append('file', file)
        const invoiceQuery = invoiceId
          ? `?invoiceId=${encodeURIComponent(invoiceId)}`
          : ''
        return {
          url: `tapi/v1/docs/load-confirmation/orders/${orderId}/reprocess-upload${invoiceQuery}`,
          method: 'POST',
          body: formData,
        }
      },
    }),

    applyLoadConfirmationReprocess: builder.mutation<
      LcReprocessApplyResult,
      LcReprocessApplyPayload
    >({
      query: ({ orderId, updatePayload, pendingId, invoiceId }) => ({
        url: `tapi/v1/docs/load-confirmation/orders/${orderId}/reprocess-apply`,
        method: 'POST',
        body: {
          updatePayload,
          pendingId,
          invoiceId: invoiceId ?? null,
        },
      }),
    }),

    cancelLoadConfirmationReprocess: builder.mutation<
      LcReprocessCancelResult,
      LcReprocessCancelPayload
    >({
      query: ({ orderId, pendingId }) => ({
        url: `tapi/v1/docs/load-confirmation/orders/${orderId}/reprocess-cancel`,
        method: 'POST',
        body: { pendingId },
      }),
    }),

    ensureInvoiceForOrder: builder.mutation<
      EnsureInvoiceForOrderResult,
      EnsureInvoiceForOrderPayload
    >({
      query: ({ orderId }) => ({
        url: `tapi/v1/orders/${orderId}/ensure-invoice`,
        method: 'POST',
      }),
    }),

    getLoadconfirmationPresignedUrl: builder.mutation<
      LoadConfirmationPresignedUrlResult,
      LoadConfirmationPresignedUrlPayload
    >({
      query: ({ orderId }) => ({
        url: `tapi/v1/docs/load-confirmation/presigned-urls/${orderId}`,
        method: 'POST',
      }),
    }),

    getPendingLoadConfirmations: builder.query<
      PendingLoadConfirmationsResult,
      PendingLoadConfirmationsPayload
    >({
      query: ({ user_id }) => ({
        url: `tapi/v1/docs/load-confirmation-temp/presigned-urls/${user_id}`,
        method: 'POST',
      }),
    }),

    getLogoPresignedUrl: builder.mutation<
      UploadLogoResponse,
      UploadLogoPayload
    >({
      query: ({ user_id }) => ({
        url: `tapi/v1/logo/presigned-urls?user_id=${user_id}`,
        method: 'POST',
      }),
    }),

    deletePendingLoadConfirmation: builder.mutation<
      DeletePendingLoadConfirmationResult,
      DeletePendingLoadConfirmationPayload
    >({
      query: ({ s3Key }) => ({
        url: 'tapi/v1/docs/load-confirmation-temp',
        method: 'DELETE',
        body: {
          s3_key: s3Key,
        },
      }),
    }),
  }),
})

export const {
  useUploadLoadConfirmationMutation,
  useUploadLoadConfirmationToOrderMutation,
  useReprocessLoadConfirmationForOrderMutation,
  useApplyLoadConfirmationReprocessMutation,
  useCancelLoadConfirmationReprocessMutation,
  useEnsureInvoiceForOrderMutation,
  useGetLoadconfirmationPresignedUrlMutation,
  useGetPendingLoadConfirmationsQuery,
  useGetLogoPresignedUrlMutation,
  useDeletePendingLoadConfirmationMutation,
} = taskAutomationApi
